import { useCallback, useEffect, useState } from "react";
import {
  fetchBioFlowQuotations,
  fetchBioFlowSubscriptionStatus,
} from "src/api/bioflow";
import type {
  BioFlowBillingCycle,
  BioFlowPlanQuotation,
  BioFlowSubscriptionStatus,
} from "src/types/bioflow";

export function useBioFlowSubscription() {
  const [status, setStatus] = useState<BioFlowSubscriptionStatus | null>(null);
  const [quotations, setQuotations] = useState<BioFlowPlanQuotation[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshStatus = useCallback(async (): Promise<BioFlowSubscriptionStatus | null> => {
    try {
      const statusRes = await fetchBioFlowSubscriptionStatus();
      setStatus(statusRes);
      return statusRes;
    } catch {
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    await refreshStatus();
    try {
      const quotationRes = await fetchBioFlowQuotations();
      setQuotations(Array.isArray(quotationRes) ? quotationRes : []);
    } catch {
      setQuotations([]);
    } finally {
      setLoading(false);
    }
  }, [refreshStatus]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const getQuotationForCycle = useCallback(
    (cycle: BioFlowBillingCycle): BioFlowPlanQuotation | undefined =>
      quotations.find((q) => q.billingCycle === cycle),
    [quotations],
  );

  return {
    status,
    quotations,
    loading,
    refresh,
    refreshStatus,
    isActive: !!status?.active,
    getQuotationForCycle,
  };
}
